"use client";

import Image from "next/image";

import ChevronRightIcon from "@repo/ui-system/icons/arrows/chevron-right/outline.svg";

import { type OutboundBiParams } from "@/lib/affiliate/build-redirect-url";
import { cn } from "@/lib/utils";

import { OutboundLink } from "../../outbound-link/outbound-link";

export type Props = {
  title: string;
  brand: string;
  imageUrl: string;
  badge?: {
    text: string;
    variant: "regular" | "first";
  };
  showSaleBadge?: boolean;
  url: string;
  storeName: string;
  bi: OutboundBiParams;
};

export function ProductCard({
  title,
  brand,
  imageUrl,
  badge,
  showSaleBadge = false,
  url,
  storeName,
  bi,
}: Props) {
  return (
    <OutboundLink
      url={url}
      storeName={storeName}
      bi={bi}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col gap-2 shrink-0 w-[200px]"
    >
      <div className="bg-surface-grayscale-subtle border border-border-grayscale-default rounded-xl p-3 relative overflow-hidden transition-colors duration-300 ease-in-out lg:group-hover:border-border-grayscale-darker lg:group-hover:bg-[#F9F9F9]">
        <div className="flex flex-col items-center justify-center px-7">
          <div className="size-[120px] mix-blend-darken">
            <Image
              src={imageUrl}
              alt={title}
              width={120}
              height={120}
              sizes="120px"
              className="size-[120px] object-contain"
            />
          </div>
        </div>

        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {badge && (
            <span
              className={cn(
                "px-3 py-1 rounded-lg text-sm font-semibold leading-5",
                badge.variant === "first" &&
                  "bg-surface-secondary-subtle text-text-secondary-label",
                badge.variant === "regular" &&
                  "bg-surface-primary-lighter text-text-primary-label",
              )}
            >
              {badge.text}
            </span>
          )}
          {showSaleBadge && (
            <span className="bg-badge-sale-bg text-badge-sale-text px-3 py-1 rounded-lg text-sm font-semibold">
              Sale
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-1 w-full">
          <p className="sub1 line-clamp-2 h-[2lh] text-[#3f3f3f]">{title}</p>
          <p className="body2 truncate text-text-grayscale-caption">{brand}</p>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <span className="inline-flex items-center gap-0.5 bg-(--cta-color,var(--color-cta)) sm:hover:bg-(--cta-color-hover,var(--color-cta-hover)) transition-colors text-grayscale-white rounded-[7px] px-2 py-1 body2 w-fit font-semibold">
            View deal
            <ChevronRightIcon className="size-4" />
          </span>
        </div>
      </div>
    </OutboundLink>
  );
}
